import { TrackerStatus } from "./order-tracking-tracker-status-definition";

export enum OrderTrackingOrderSource {
  SHOPIFY = "shopify",
  BIGCOMMERCE = "bigcommerce",
  COMMENTSOLD = "commentsold",
}

export enum OrderTrackingIntegrationType {
  REDO = "redo",
  KLAVIYO = "klaviyo",
  ATTENTIVE = "attentive",
  POSTSCRIPT = "postscript",
}

export enum OrderTrackingOrderStatus {
  CREATED = "created",
  FULFILLED = "fulfilled",
  STALLED_IN_FULFILLMENT = "stalled-in-fulfillment",
  CANCELLED = "cancelled",
}

export enum OrderTrackingShipmentStatus {
  UNKNOWN = "unknown",
  PENDING = "pending",
  PRE_TRANSIT = "pre_transit",
  IN_TRANSIT = "in_transit",
  OUT_FOR_DELIVERY = "out_for_delivery",
  DELIVERED = "delivered",
  AVAILABLE_FOR_PICKUP = "available_for_pickup",
  AVAILABLE_FOR_PICKUP_CARRIER = "available_for_pickup_carrier",
  STALLED_IN_TRANSIT = "stalled_in_transit",
  RETURN_TO_SENDER = "return_to_sender",
  DELIVERY_ATTEMPTED = "delivery_attempted",
  DELIVERY_FAILURE = "delivery_failure",
  SHIPMENT_CANCELLED = "shipment_cancelled",
  SHIPMENT_ERROR = "shipment_error",
}

export interface OrderTrackingOrderWebhookPayload {
  teamId: string;
  source: OrderTrackingOrderSource;
  orderStatus: OrderTrackingOrderStatus;
}

export interface OrderTrackingDispatchEventResult {
  dispatchedAt: Date;
  integrationType: OrderTrackingIntegrationType;
  success: boolean;
  message?: string;
}

export enum OrderTrackingEntityType {
  ORDER = "order",
  SHIPMENT = "shipment",
  TRACKER = "tracker",
}

export enum OrderTrackingEventCategory {
  WEBHOOK_RECEIVED = "webhook_received",
  DISPATCH_SENT = "dispatch_sent",
}

export enum OrderTrackingEventSource {
  // Platforms
  SHOPIFY = "shopify",
  BIGCOMMERCE = "bigcommerce",
  COMMENTSOLD = "commentsold",

  // Tracking providers
  EASYPOST = "easypost",
  TRACK123 = "track123",
  TRACK17 = "track17",
  SHIPPO = "shippo",
  SHIP24 = "ship24",

  // Integration destinations
  KLAVIYO = "klaviyo",
  ATTENTIVE = "attentive",
  POSTSCRIPT = "postscript",
  REDO = "redo",
}

export enum OrderTrackingOrderUpdateType {
  CREATED = "created",
  UPDATED = "updated",
  FULFILLED = "fulfilled",
  CANCELLED = "cancelled",
  LINE_ITEMS_UPDATED = "line_items_updated",
}

export enum OrderTrackingOrderFulfillmentStatus {
  UNFULFILLED = "unfulfilled",
  PARTIALLY_FULFILLED = "partially_fulfilled",
  FULFILLED = "fulfilled",
}

/**
 * Statuses that should dispatch to integrations on every occurrence.
 * All other statuses only dispatch on first occurrence.
 */
export const ALWAYS_DISPATCH_STATUSES = new Set<OrderTrackingShipmentStatus>([
  OrderTrackingShipmentStatus.DELIVERY_FAILURE,
  OrderTrackingShipmentStatus.STALLED_IN_TRANSIT,
]);

/**
 * Maps OrderTrackingShipmentStatus to the TrackerStatus stored in tracking events.
 * Used to check existing tracking events for dispatch eligibility.
 * null = no corresponding tracker status (workflow-internal or dead code).
 */
export const SHIPMENT_STATUS_TO_TRACKER_STATUS: Record<
  OrderTrackingShipmentStatus,
  TrackerStatus | null
> = {
  [OrderTrackingShipmentStatus.UNKNOWN]: null,
  [OrderTrackingShipmentStatus.PENDING]: null,
  [OrderTrackingShipmentStatus.PRE_TRANSIT]: TrackerStatus.PRE_TRANSIT,
  [OrderTrackingShipmentStatus.IN_TRANSIT]: TrackerStatus.IN_TRANSIT,
  [OrderTrackingShipmentStatus.OUT_FOR_DELIVERY]:
    TrackerStatus.OUT_FOR_DELIVERY,
  [OrderTrackingShipmentStatus.DELIVERED]: TrackerStatus.DELIVERED,
  [OrderTrackingShipmentStatus.AVAILABLE_FOR_PICKUP]:
    TrackerStatus.AVAILABLE_FOR_PICKUP,
  [OrderTrackingShipmentStatus.AVAILABLE_FOR_PICKUP_CARRIER]: null,
  [OrderTrackingShipmentStatus.STALLED_IN_TRANSIT]: null,
  [OrderTrackingShipmentStatus.RETURN_TO_SENDER]:
    TrackerStatus.RETURN_TO_SENDER,
  [OrderTrackingShipmentStatus.DELIVERY_ATTEMPTED]: null,
  [OrderTrackingShipmentStatus.DELIVERY_FAILURE]: TrackerStatus.FAILURE,
  [OrderTrackingShipmentStatus.SHIPMENT_CANCELLED]: TrackerStatus.CANCELLED,
  [OrderTrackingShipmentStatus.SHIPMENT_ERROR]: TrackerStatus.ERROR,
};

/**
 * Maps TrackerStatus (from external providers) to OrderTrackingShipmentStatus.
 * undefined = no corresponding shipment status (e.g. UNKNOWN).
 */
export const TRACKER_STATUS_TO_SHIPMENT_STATUS: Record<
  TrackerStatus,
  OrderTrackingShipmentStatus | undefined
> = {
  [TrackerStatus.UNKNOWN]: undefined,
  [TrackerStatus.PRE_TRANSIT]: OrderTrackingShipmentStatus.PRE_TRANSIT,
  [TrackerStatus.IN_TRANSIT]: OrderTrackingShipmentStatus.IN_TRANSIT,
  [TrackerStatus.OUT_FOR_DELIVERY]:
    OrderTrackingShipmentStatus.OUT_FOR_DELIVERY,
  [TrackerStatus.DELIVERED]: OrderTrackingShipmentStatus.DELIVERED,
  [TrackerStatus.AVAILABLE_FOR_PICKUP]:
    OrderTrackingShipmentStatus.AVAILABLE_FOR_PICKUP_CARRIER,
  [TrackerStatus.RETURN_TO_SENDER]:
    OrderTrackingShipmentStatus.RETURN_TO_SENDER,
  [TrackerStatus.FAILURE]: OrderTrackingShipmentStatus.DELIVERY_FAILURE,
  [TrackerStatus.CANCELLED]: OrderTrackingShipmentStatus.SHIPMENT_CANCELLED,
  [TrackerStatus.ERROR]: OrderTrackingShipmentStatus.SHIPMENT_ERROR,
};

/**
 * Number of hours to suppress integration dispatches for backfilled orders.
 * During this period, state transitions and CRDB writes still happen,
 * but no notifications are sent to integrations (Klaviyo, Attentive, etc.).
 */
export const BACKFILL_QUIET_PERIOD_HOURS = 2;

/**
 * Default number of days to backfill orders when starting OTv2 workflows.
 */
export const OTV2_BACKFILL_DAYS = 30;
